import React, { useState, useEffect, useRef, useCallback } from 'react';
import { documentAPI } from '../../services/api';
import SignaturePad from './SignaturePad';
import './documentEditor.css';

// Edit a generated document's HTML source in a rich contentEditable editor
// (mirrors the email composer's toolbar), then save -> re-render PDF server-side.
// A drawn signature is dropped into the document's signature slot ([data-prc-sign])
// or, failing that, at the caret.
const DocumentEditor = ({ documentId, fileName, onClose, onSaved, autoSign = false, initialHtml = null }) => {
    const editorRef = useRef(null);
    const savedRange = useRef(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [showSignPad, setShowSignPad] = useState(false);

    useEffect(() => {
        let cancelled = false;
        // For an uploaded PDF we pass the extracted HTML directly; generated docs
        // fetch their stored source.
        if (initialHtml != null) {
            if (editorRef.current) editorRef.current.innerHTML = initialHtml;
            setLoading(false);
            if (autoSign) setShowSignPad(true);
            return () => { cancelled = true; };
        }
        documentAPI.getHtml(documentId)
            .then(({ data }) => {
                if (cancelled) return;
                if (editorRef.current) editorRef.current.innerHTML = data.html || '';
                setLoading(false);
                if (autoSign) setShowSignPad(true);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err.response?.data?.message || 'Failed to load document.');
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [documentId, autoSign, initialHtml]);

    const exec = (command, value = null) => {
        editorRef.current?.focus();
        document.execCommand(command, false, value);
    };

    // Remember the caret so a signature can be inserted where the user last was.
    const rememberSelection = useCallback(() => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
            savedRange.current = sel.getRangeAt(0).cloneRange();
        }
    }, []);

    const insertLink = () => {
        const url = window.prompt('Link URL:');
        if (url) exec('createLink', url);
    };

    const applySignature = (dataUrl) => {
        setShowSignPad(false);
        const editor = editorRef.current;
        if (!editor) return;
        const imgHtml = `<img src="${dataUrl}" alt="Signature" style="max-height:64px;display:inline-block;" />`;
        // Prefer a designated signature slot; otherwise drop at the remembered caret.
        const slot = editor.querySelector('[data-prc-sign]');
        if (slot) {
            slot.innerHTML = imgHtml;
        } else {
            editor.focus();
            const sel = window.getSelection();
            if (savedRange.current) {
                sel.removeAllRanges();
                sel.addRange(savedRange.current);
            }
            document.execCommand('insertHTML', false, imgHtml);
        }
    };

    const handleSave = async () => {
        if (!editorRef.current) return;
        setSaving(true);
        setError('');
        try {
            await documentAPI.updateHtml(documentId, editorRef.current.innerHTML);
            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save document.');
            setSaving(false);
        }
    };

    return (
        <div className="doc-editor-overlay" onClick={onClose}>
            <div className="doc-editor-modal" onClick={(e) => e.stopPropagation()}>
                <div className="doc-editor-header">
                    <h3><i className="fas fa-file-pdf" /> Edit: {fileName}</h3>
                    <button className="doc-editor-close" onClick={onClose}>&times;</button>
                </div>

                {error && <div className="doc-editor-error">{error}</div>}

                <div className="doc-editor-toolbar">
                    <button type="button" className="toolbar-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')} title="Bold"><b>B</b></button>
                    <button type="button" className="toolbar-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('italic')} title="Italic"><i>I</i></button>
                    <button type="button" className="toolbar-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('underline')} title="Underline"><u>U</u></button>
                    <span className="toolbar-divider" />
                    <button type="button" className="toolbar-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertUnorderedList')} title="Bullet List"><i className="fas fa-list-ul" /></button>
                    <button type="button" className="toolbar-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertOrderedList')} title="Numbered List"><i className="fas fa-list-ol" /></button>
                    <span className="toolbar-divider" />
                    <button type="button" className="toolbar-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('justifyLeft')} title="Align Left"><i className="fas fa-align-left" /></button>
                    <button type="button" className="toolbar-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('justifyCenter')} title="Align Center"><i className="fas fa-align-center" /></button>
                    <button type="button" className="toolbar-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('justifyRight')} title="Align Right"><i className="fas fa-align-right" /></button>
                    <span className="toolbar-divider" />
                    <button type="button" className="toolbar-btn" onMouseDown={(e) => e.preventDefault()} onClick={insertLink} title="Insert Link"><i className="fas fa-link" /></button>
                    <label className="toolbar-btn" title="Text Color" onMouseDown={(e) => e.preventDefault()}>
                        <i className="fas fa-palette" />
                        <input type="color" style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }} onChange={(e) => exec('foreColor', e.target.value)} />
                    </label>
                    <span className="toolbar-divider" />
                    <button type="button" className="toolbar-btn toolbar-sign" onMouseDown={(e) => e.preventDefault()} onClick={() => { rememberSelection(); setShowSignPad(true); }} title="Add Signature">
                        <i className="fas fa-signature" /> Sign
                    </button>
                </div>

                <div className="doc-editor-body">
                    {loading && <div className="doc-editor-loading">Loading…</div>}
                    <div
                        ref={editorRef}
                        className="doc-editor-canvas"
                        contentEditable={!loading}
                        suppressContentEditableWarning
                        onKeyUp={rememberSelection}
                        onMouseUp={rememberSelection}
                    />
                </div>

                <div className="doc-editor-actions">
                    <button className="btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="btn-primary" onClick={handleSave} disabled={saving || loading}>
                        {saving ? 'Rendering…' : 'Save & Re-render PDF'}
                    </button>
                </div>
            </div>

            {showSignPad && (
                <SignaturePad onSave={applySignature} onCancel={() => setShowSignPad(false)} />
            )}
        </div>
    );
};

export default DocumentEditor;
